import * as React from "react";

type SetValue<Value> = React.Dispatch<React.SetStateAction<Value>>;

interface UseLocalStorageOptions<Value> {
  /** The value used during SSR and the initial hydration render. */
  serverValue?: Value;
}

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(
      `readLocalStorage: Fetching item from localStorage failed: ${key}`
    );
    console.error(error);
    return null;
  }
}

function writeLocalStorage<Value>(key: string, value: Value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(
      `writeLocalStorage: Saving item to localStorage failed: ${key}`
    );
    console.error(error);
  }
}

function parseLocalStorage<Value>(
  value: string | null,
  fallback: Value
): Value {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function subscribeToLocalStorageChanges(key: string) {
  return (callback: () => void): (() => void) => {
    const handleStorageChange = (event: StorageEvent) => {
      // Only trigger callback if the key matches
      if (event.key === key) {
        callback();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  };
}

/**
 * Read and write to localStorage using React state. This also subscribes to
 * changes in other tabs/windows.
 *
 * Note: The subscription to changes only works for changes in other
 * tabs/windows. If the value is changed in the same tab/window outside of this
 * hook, the value will not be updated automatically.
 */
export function useLocalStorage<Value>(
  key: string,
  initialValue: Value,
  { serverValue = initialValue }: UseLocalStorageOptions<Value> = {}
): [Value, SetValue<Value>] {
  // Used to react to changes both when the value is changed and when
  // localStorage is changed in other tabs/windows
  const handleSubscription = React.useMemo(
    () => subscribeToLocalStorageChanges(key),
    [key]
  );

  // Get the current value from localStorage
  const getSnapshot = React.useCallback(() => readLocalStorage(key), [key]);

  // Get the current value from the server during SSR or static generation
  const getServerSnapshot = React.useCallback(
    () => JSON.stringify(serverValue),
    [serverValue]
  );

  const serializedValue = React.useSyncExternalStore(
    handleSubscription,
    getSnapshot,
    getServerSnapshot
  );

  const value = parseLocalStorage(serializedValue, initialValue);

  const setValue = React.useCallback(
    (getNextValue: React.SetStateAction<Value>) => {
      // Allow value to be a function to keep the same API as useState
      const nextValue =
        getNextValue instanceof Function ? getNextValue(value) : getNextValue;

      writeLocalStorage(key, nextValue);

      // Notify other instances of the app that the value has changed
      window.dispatchEvent(
        new StorageEvent("storage", {
          key,
          newValue: JSON.stringify(nextValue),
        })
      );
    },
    [value, key]
  );

  return [value, setValue];
}
